import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, push, set, get, update } from 'firebase/database';
import '../pagesCSS/list.css';
import '../pagesCSS/list.mobile.css'; 
import ListModal from '../components/modalList';
import EditEntryForm from '../components/editEntryForm';
import companyDictionary from '../Assets/companyDictionary.json';
import placeholderCompanyImage from '../Assets/Images/blank_profile.png';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import Swal from 'sweetalert2';

function normalize(input) {
    return input
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }
  
const aliasToCompanyId = {};

Object.entries(companyDictionary).forEach(([companyId, company]) => {
  company.aliases.forEach(alias => {
    aliasToCompanyId[normalize(alias)] = companyId;
  });
});

const List = () => {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('User');
  const [userEntries, setUserEntries] = useState([]);
  const auth = getAuth();
  const db = getDatabase();
  const [showModal, setShowModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    openToContact: false,
    name: '',
    preferredContact: '',
    yearApplied: '',
    gpa: '',
    companyName: '',
    roleTitle: '',
    positionType: '',
    startDate: '',
    applicationMethod: '',
    hiringProcessLength: '',
    interviewProcess: '',
    questionsAsked: '',
    hourlyRate: '',
    baseSalary: '',
    totalCompensation: '',
    location: ''
  });
  const [userProfile, setUserProfile] = useState({
    name: '',
    preferredContact: '',
    yearApplied: '',
    gpa: ''
  });

  const getCompanyLogo = (companyName) => {
    if (!companyName) return placeholderCompanyImage;

    const normalized = normalize(companyName);
    const companyId = aliasToCompanyId[normalized];

    if (!companyId || !companyDictionary[companyId]) {
      return placeholderCompanyImage;
    }

    const logoPath = companyDictionary[companyId].logo;

    try {
      return require(`../Assets/Images/CompanyImages/${logoPath}`);
    } catch {
      return placeholderCompanyImage;
    }
  };



  // Authentication and username fetch
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userId = currentUser.uid;
        setUser(currentUser);
        try {
          const userRef = ref(db, `users/${userId}/username`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            setUsername(snapshot.val());
          }
        } catch (error) {
          console.error("Error fetching username:", error);
        }
      } else {
        setUser(null);
        setUsername('User');
      }
    });
    return () => unsubscribe();
  }, [auth, db]);

  // Fetch user entries
  useEffect(() => {
    const fetchUserEntries = async () => {
      if (!user) {
        setUserEntries([]);
        return;
      }

      try {
        const userInterviewsRef = ref(db, `interviews/${user.uid}`);
        const snapshot = await get(userInterviewsRef);
        
        if (snapshot.exists()) {
          const entries = [];
          snapshot.forEach((childSnapshot) => {
            entries.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          entries.sort((a, b) => b.timestamp - a.timestamp);
          setUserEntries(entries);
        } else {
          setUserEntries([]);
        }
      } catch (error) {
        console.error("Error fetching user entries:", error);
      }
    };

    fetchUserEntries();
  }, [user, db]);

  useEffect(() => {
    if (!user) {
      setUserProfile({ name: '', preferredContact: '', yearApplied: '', gpa: '' });
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser) {
          setUser(currentUser);
          const userId = currentUser.uid;
          try {
            const userRef = ref(db, `users/${userId}`);
            const snapshot = await get(userRef);
            if (snapshot.exists()) {
              const userData = snapshot.val();
              setUserProfile({
                name: userData.username || '',
                preferredContact: userData.preferredContact || '',
                yearApplied: userData.currentYear || '',
                gpa: userData.gpa || ''
              });
              
            }
          } catch (error) {
            console.error('Error fetching user:', error);
          }
        } else {
          setUser(null);
          setUsername('User');
        }
      });
      return () => unsubscribe();
  }, [user, db]);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateEntry = async (updatedEntry) => {
    try {
      const entryRef = ref(db, `interviews/${user.uid}/${updatedEntry.id}`);
      await update(entryRef, updatedEntry);
      // Update the local state
      setUserEntries(prev =>
        prev.map(e => (e.id === updatedEntry.id ? updatedEntry : e))
      );
      setEditingEntry(null);
      Swal.fire({
        title: "Success!",
        text: "Thank you for contributing to WiCSEpedia.",
        icon: "success"
      });
    } catch (error) {
      console.error("Error updating entry:", error);
      Swal.fire({
        title: "Error updating entry!",
        icon: "error"
      });
    }
  };

  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    const errors = {};

    // Validate all required fields
    Object.entries(formData).forEach(([key, value]) => {
      // All fields are required except name, preferredContact, and openToContact
      if (
        key !== 'name' &&
        key !== 'preferredContact' &&
        key !== 'openToContact' &&
        !value
      ) {
        errors[key] = 'This field is required';
      }

      // If openToContact is true, then name, and preferredContact are also required
      if (
        formData.openToContact &&
        (key === 'name' || key === 'preferredContact') &&
        !value
      ) {
        errors[key] = 'This field is required';
      }
    });

    if (formData.openToContact) {
      if (!formData.name) {
        errors.name = 'Name is required when open to contact';
      }
      if (!formData.preferredContact) {
        errors.preferredContact = 'Email/phone number is required when open to contact';
      }
    }
    console.log(errors);

    // Validate word count for interview process and questions
    if (countWords(formData.interviewProcess) < 20) {
      errors.interviewProcess = 'Please provide at least 20 words for the interview process description';
    }

    if (countWords(formData.questionsAsked) < 20) {
      errors.questionsAsked = 'Please provide at least 20 words for the questions asked';
    }

    // If there are errors, show them and stop submission
    if (Object.keys(errors).length > 0) {
      console.log(errors);
      setFormErrors(errors);
      // Scroll to the first error
      const firstErrorField = document.querySelector('.error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      const userInterviewsRef = ref(db, `interviews/${user.uid}`);
      const newInterviewRef = push(userInterviewsRef);
      
      console.log("RAW interviewProcess:", formData.interviewProcess);
      console.log("JSON:", JSON.stringify(formData.interviewProcess));
      await set(newInterviewRef, {
        timestamp: Date.now(),
        ...formData
      });

      // Reset form and close modal first
      setFormData({
        openToContact: false,
        name: '',
        preferredContact: '',
        yearApplied: '',
        gpa: '',
        companyName: '',
        roleTitle: '',
        positionType: '',
        startDate: '',
        applicationMethod: '',
        hiringProcessLength: '',
        interviewProcess: '',
        questionsAsked: '',
        hourlyRate: '',
        baseSalary: '',
        totalCompensation: '',
        location: ''
      });
      setFormErrors({});
      setShowModal(false); // Close the modal immediately
      
      Swal.fire({
        title: "Success!",
        text: "Thank you for contributing to WiCSEpedia.",
        icon: "success"
      });
    } catch (error) {
      console.error("Error submitting interview data:", error);
      Swal.fire({
        title: "Error submitting data!",
        icon: "error"
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCardId((prevId) => (prevId === cardId ? null : cardId));
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="interview-form">
      <div className="form-group">
        <label htmlFor="openToContact">
          <input
            id='openToContact'
            type="checkbox"
            name="openToContact"
            checked={formData.openToContact || false}
            onChange={handleInputChange}
          />
          Open to be contacted
        </label>
      </div>

      {formData.openToContact && (
        <>
          <div className="form-group">
            <label htmlFor="name">Name <strong style={{ color: " #850808" }}>*</strong></label> 
            <input
              id = "name"
              type="text"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
            />
            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="preferredContact">Best Form of Contact <strong style={{ color: " #850808" }}>*</strong></label>
            <input
              id = "preferredContact" 
              name="preferredContact"
              value={formData.preferredContact || ''}
              onChange={handleInputChange}
              placeholder="e.g. Email / Phone number"
            />
            {formErrors.preferredContact && <span className="error-message">{formErrors.preferredContact}</span>}
          </div>
        </>
      )}

      <div className="form-group">
        <label htmlFor="yearApplied">Year When Applied <strong style={{ color: " #a20c0c" }}>*</strong></label>
        <input
          id = "yearApplied"
          type="text"
          name="yearApplied"
          value={formData.yearApplied}
          onChange={handleInputChange}
          placeholder="e.g., Sophomore, Junior"
          className={formErrors.yearApplied ? 'error' : ''}
        />
        {formErrors.yearApplied && <span className="error-message">{formErrors.yearApplied}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="gpa">GPA <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "gpa"
          type="text"
          name="gpa"
          value={formData.gpa}
          onChange={handleInputChange}
          placeholder="Your GPA"
          className={formErrors.gpa ? 'error' : ''}
        />
        {formErrors.gpa && <span className="error-message">{formErrors.gpa}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="companyName">Company Name <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "companyName"
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleInputChange}
          placeholder="Company name"
          className={formErrors.companyName ? 'error' : ''}
        />
        {formErrors.companyName && <span className="error-message">{formErrors.companyName}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="roleTitle">Role Title <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "roleTitle"
          type="text"
          name="roleTitle"
          value={formData.roleTitle}
          onChange={handleInputChange}
          placeholder="e.g., Software Engineer Intern"
          className={formErrors.roleTitle ? 'error' : ''}
        />
        {formErrors.roleTitle && <span className="error-message">{formErrors.roleTitle}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="positionType">Position Type <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "positionType"
          type="text"
          name="positionType"
          value={formData.positionType}
          onChange={handleInputChange}
          placeholder="e.g., Internship, Full-time"
          className={formErrors.positionType ? 'error' : ''}
        />
        {formErrors.positionType && <span className="error-message">{formErrors.positionType}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="startDate">Start Date <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "startDate"
          type="date"
          name="startDate"
          value={formData.startDate}
          onChange={handleInputChange}
          className={formErrors.startDate ? 'error' : ''}
        />
        {formErrors.startDate && <span className="error-message">{formErrors.startDate}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="applicationMethod">How did you apply? <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "applicationMethod"
          type="text"
          name="applicationMethod"
          value={formData.applicationMethod}
          onChange={handleInputChange}
          placeholder="e.g., Career fair, Online application"
          className={formErrors.applicationMethod ? 'error' : ''}
        />
        {formErrors.applicationMethod && <span className="error-message">{formErrors.applicationMethod}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="hiringProcessLength">Length of Hiring Process <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "hiringProcessLength"
          type="text"
          name="hiringProcessLength"
          value={formData.hiringProcessLength}
          onChange={handleInputChange}
          placeholder="e.g., 2 weeks, 1 month"
          className={formErrors.hiringProcessLength ? 'error' : ''}
        />
        {formErrors.hiringProcessLength && <span className="error-message">{formErrors.hiringProcessLength}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="interviewProcess">Interview Process Description <strong style={{ color: " #850808" }}>*</strong></label>
        <textarea
          id = "interviewProcess"
          name="interviewProcess"
          value={formData.interviewProcess}
          onChange={handleInputChange}
          placeholder="Describe the interview process (minimum 20 words)"
          rows="4"
          className={formErrors.interviewProcess ? 'error' : ''}
        />
        {formErrors.interviewProcess && <span className="error-message">{formErrors.interviewProcess}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="questionsAsked">Questions Asked <strong style={{ color: " #850808" }}>*</strong></label>
        <textarea
          id = "questionsAsked"
          name="questionsAsked"
          value={formData.questionsAsked}
          onChange={handleInputChange}
          placeholder="List the questions you were asked (minimum 20 words)"
          rows="4"
          className={formErrors.questionsAsked ? 'error' : ''}
        />
        {formErrors.questionsAsked && <span className="error-message">{formErrors.questionsAsked}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="hourlyRate">Hourly Rate <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "hourlyRate"
          type="text"
          name="hourlyRate"
          value={formData.hourlyRate}
          onChange={handleInputChange}
          placeholder="e.g., $25/hour"
          className={formErrors.hourlyRate ? 'error' : ''}
        />
        {formErrors.hourlyRate && <span className="error-message">{formErrors.hourlyRate}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="baseSalary">Base Salary (if applicable) <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "baseSalary"
          type="text"
          name="baseSalary"
          value={formData.baseSalary}
          onChange={handleInputChange}
          placeholder="e.g., $75,000"
          className={formErrors.baseSalary ? 'error' : ''}
        />
        {formErrors.baseSalary && <span className="error-message">{formErrors.baseSalary}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="totalCompensation">Total Compensation <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "totalCompensation"
          type="text"
          name="totalCompensation"
          value={formData.totalCompensation}
          onChange={handleInputChange}
          placeholder="Include salary, bonus, stock options, etc."
          className={formErrors.totalCompensation ? 'error' : ''}
        />
        {formErrors.totalCompensation && <span className="error-message">{formErrors.totalCompensation}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="location">Location <strong style={{ color: " #850808" }}>*</strong></label>
        <input
          id = "location"
          type="text"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          placeholder="City, State or Remote"
          className={formErrors.location ? 'error' : ''}
        />
        {formErrors.location && <span className="error-message">{formErrors.location}</span>}
      </div>

      <button type="submit" className="submit-button">Submit Interview Experience</button>
    </form>
  );

  const modalContent = (
    <div>
      <h2>Interview Experience Submission</h2>
      {formContent}
    </div>
  );

  const UserEntries = () => (
    <div className="entries-container">
      <h2 className="entries-title">Your Previous Entries</h2>
      {userEntries.length === 0 ? (
        <p className="no-entries">No entries yet. Submit your first interview experience!</p>
      ) : (
        <div className="entries-grid">
          {userEntries.map((entry) => {
            const logoSrc = getCompanyLogo(entry.companyName);

            return (
            <div key={entry.id} className="entry-card">
              <div className="entry-header">
                <div className="header-left">
                  <img className="company-logo"
                    src={logoSrc}
                    alt={`${entry.companyName} logo`}
                    onError={(e) => e.target.src = placeholderCompanyImage} // if the image fails to load
                  />
                  <div className="title-and-location">
                    <h3 className="company-name">
                      {entry.companyName}, {entry.roleTitle}
                    </h3>
                    <p className="location-and-type" style={{ color: '#292525' }}>
                      {entry.location} • {entry.positionType}
                    </p>
                  </div>
                </div>
                <div className="header-right">
                  Posted on {formatDate(entry.timestamp)}
                  <button className="edit-button" style={{ paddingLeft: '12px', paddingRight: '12px', marginLeft: '12px', marginRight: '12px'}}
                    onClick={() => {
                    setEditingEntry(entry);
                    setShowEditModal(true);
                    }}>Edit</button>
                </div>
                
              </div>

              <div className="card-horizontal-body">
                <div className="left-column">
                  <div className="details-row">
                    <div>
                      <div className="detail-item">
                        <strong>GPA:</strong> {entry.gpa}
                      </div>
                      <div className="detail-item">
                        <strong>Year Applied:</strong> {entry.yearApplied}
                      </div>
                      <div className="detail-item">
                        <strong>Application Method:</strong> {entry.applicationMethod}
                      </div>
                      <div className="detail-item">
                        <strong>Hiring Process Length:</strong> {entry.hiringProcessLength}
                      </div>
                    </div>
                    <div>
                      {entry.hourlyRate && (
                        <div className="detail-item">
                          <strong>Hourly Rate:</strong> {entry.hourlyRate}
                        </div>
                      )}
                      {entry.baseSalary && (
                        <div className="detail-item">
                          <strong>Base Salary:</strong> {entry.baseSalary}
                        </div>
                      )}
                      {entry.totalCompensation && (
                        <div className="detail-item">
                          <strong>Total Compensation:</strong> {entry.totalCompensation}
                        </div>
                      )}
                      {entry.openToContact && (
                        <>
                          <div className="detail-item">
                            <strong>Name:</strong> {entry.name || 'N/A'}
                          </div>
                          <div className="detail-item">
                            <strong>Contact:</strong> {entry.preferredContact || 'N/A'}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="see-more-details">
                <span className="details-link" onClick={() => toggleCardExpansion(entry.id)}>
                  <div className="details-text">{expandedCardId === entry.id ? "Hide details" : "See more details"}</div>
                  <div className={`down-arrow ${expandedCardId === entry.id ? 'rotated' : ''}`}>▼</div>
                </span>
              </div>
              {expandedCardId === entry.id && (
              <div className="full-essay-section expanded">
                <div className="essay-section">
                  <strong>Interview Process:</strong>
                  <p className="essay-text-full">{entry.interviewProcess}</p>
                </div>
                <div className="essay-section">
                  <strong>Questions Asked:</strong>
                  <p className="essay-text-full">{entry.questionsAsked}</p>
                </div>
              </div>
            )}

            </div>
          )})}
        </div>
      )}
    </div>
  );

  return (
    <div className="savedBody">
      {user ? (
        <div className="entries-page">
          <h1 className="page-title">
            Share your interviewing experiences!
          </h1>


          <div>
            <button
              className="button-36"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  openToContact: true,
                  name: userProfile.name,
                  preferredContact: userProfile.preferredContact,
                  yearApplied: userProfile.yearApplied,
                  gpa: userProfile.gpa
                }));
                setShowModal(true);
              }}
            >
              Submit Data
            </button>

            {showModal && (
              <ListModal show={showModal} onClose={handleCloseModal}>
                {modalContent}
              </ListModal>
            )}
          </div>

          <div className="entries-section">
            <UserEntries />
          </div>
        </div>
      ) : (
        <motion.div 
      className="auth-prompt"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{height: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}
    >
      {/* Decorative gradient background */}
      <div className="auth-gradient-bg"></div>
      
      {/* Icon */}
      <motion.div 
        className="auth-icon"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <Lock className="lock-icon" />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="auth-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="auth-title">
          Welcome to WiCSEpedia
        </h2>
        <p className="auth-subtitle">
          Please sign in to access and contribute to our interview database
        </p>
      </motion.div>

      {/* Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Link to="/userAuth" className="auth-button">
          <span>Sign In to Continue</span>
        </Link>
      </motion.div>

    </motion.div>
      )}

      {showEditModal && editingEntry && (
        <ListModal show={true} onClose={() => setEditingEntry(null)}>
          <EditEntryForm
            entry={editingEntry}
            onSubmit={handleUpdateEntry} // <-- Make sure this is defined
            onCancel={() => setEditingEntry(null)}
          />
        </ListModal>
      )}
    </div>
  );
};

export default List;